import React, { useState, useEffect, useRef } from 'react';
import { Eye, Cog, Gauge, BarChart2, Zap, Cloud, Bot, Server, ChevronRight, Droplets, Fuel, Filter, Bolt, TrendingUp, PieChart } from 'lucide-react';
import { FindItSVG, BuildItSVG, UseItSVG } from './FeatureSVGs';

const features = [
    {
        id: "extract",
        title: "Extract",
        icon: <Droplets className="w-12 h-12 text-blue-500" />,
        label: "Discover Hidden Reserves",
        sublabel: "Like crude oil beneath the surface, your business generates valuable data every day. Our specialized tools drill down to extract hidden insights from every corner of your operations.",
        color: "from-blue-600 to-blue-400",
        borderColor: "border-blue-500",
        shadowColor: "shadow-blue-500/20",
        link: "/extract",
        points: [
            "Identify untapped data reserves in your business operations",
            "Extract insights from customer interactions and behaviors",
            "Discover new data sources with high yield potential"
        ],
        tags: [
            { icon: <Eye />, text: "Data Discovery" },
            { icon: <Cog />, text: "Process Analysis" },
            { icon: <Gauge />, text: "Resource Mapping" }
        ],
        ProcessSVG: FindItSVG,
        animDelay: "0s"
    },
    {
        id: "refine",
        title: "Refine",
        icon: <Filter className="w-12 h-12 text-indigo-500" />,
        label: "Process Raw Data into Value",
        sublabel: "Raw data, like crude oil, requires sophisticated refinement. Our pipeline infrastructure transforms scattered information into structured, high-grade resources ready for strategic deployment.",
        color: "from-indigo-600 to-indigo-400",
        borderColor: "border-indigo-500",
        shadowColor: "shadow-indigo-500/20",
        link: "/refine",
        points: [
            "Purify and structure raw data through intelligent processing",
            "Connect disparate data sources into a unified pipeline system",
            "Implement secure refinement processes that scale with demand"
        ],
        tags: [
            { icon: <Server />, text: "Secure Storage" },
            { icon: <Cloud />, text: "Centralized Access" },
            { icon: <Zap />, text: "Efficient Processing" }
        ],
        ProcessSVG: BuildItSVG,
        animDelay: "2s"
    },
    {
        id: "fuel",
        title: "Fuel",
        icon: <Fuel className="w-12 h-12 text-amber-500" />,
        label: "Power Your Business Growth",
        sublabel: "Premium data fuel propels business performance. Our solutions transform refined data into high-octane insights, automated workflows, and AI-driven tools that accelerate your competitive advantage.",
        color: "from-amber-600 to-amber-400",
        borderColor: "border-amber-500",
        shadowColor: "shadow-amber-500/20",
        link: "/fuel",
        points: [
            "Power decision-making with real-time performance dashboards",
            "Automate routine operations for maximum efficiency",
            "Leverage predictive analytics to anticipate market shifts"
        ],
        tags: [
            { icon: <BarChart2 />, text: "Performance Metrics" },
            { icon: <Bot />, text: "AI Intelligence" },
            { icon: <Bolt />, text: "Process Automation" }
        ],
        ProcessSVG: UseItSVG,
        animDelay: "4s"
    }
];

// Animation for flowing data/oil in the pipeline
const PipelineFlow = ({ active }) => {
    return (
        <div className="absolute inset-0 overflow-hidden">
            {[...Array(15)].map((_, i) => (
                <div
                    key={i}
                    className={`absolute h-2 w-2 rounded-full bg-gradient-to-r from-blue-400 to-amber-400 
                              ${active ? 'animate-flow-down' : 'opacity-0'}`}
                    style={{
                        left: '50%',
                        top: `-10%`,
                        animationDelay: `${i * 0.5}s`,
                        animationDuration: '3s',
                    }}
                ></div>
            ))}
        </div>
    );
};

// Inside Features.js (or wherever FeatureCard is defined)

// ... (import statements, features array, PipelineFlow)

const FeatureCard = ({ feature, active, onNavigate, onMouseEnter }) => {
    return (
        <div
            className={`group relative overflow-hidden bg-gradient-to-b from-slate-800/80 to-slate-900/80 rounded-xl p-6 md:p-8 border transition-all duration-300
            ${active ? feature.borderColor + ' ' + feature.shadowColor : 'border-slate-700/50'}
            hover:${feature.borderColor} hover:${feature.shadowColor} cursor-pointer`}
            onClick={() => onNavigate(feature.link)}
            onMouseEnter={onMouseEnter}
        >
            {/* Subtle hover background */}
            <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>

            {/* Use flex column on small screens, row on medium+ */}
            <div className="flex flex-col md:flex-row gap-6 md:gap-8 relative z-10">
                {/* Left Content */}
                <div className="flex-1 md:max-w-[55%]"> {/* Adjusted width */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 mb-6">
                        {/* Icon */}
                        <div className={`relative flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 ${active ? feature.borderColor : 'border-slate-700'}`}>
                            <div className={`absolute inset-0 rounded-full bg-gradient-to-r ${feature.color} opacity-10 ${active ? 'animate-pulse' : ''}`} style={{ animationDuration: '3s' }}></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                {/* Scale icon slightly for visibility */}
                                {React.cloneElement(feature.icon, { className: "w-8 h-8 sm:w-10 sm:h-10" })}
                            </div>
                        </div>

                        {/* Title/Label */}
                        <div className="flex-grow">
                            <h3 className="text-2xl sm:text-3xl font-bold text-white tracking-wide mb-1">{feature.title}</h3>
                            <p className="text-md sm:text-lg text-slate-200 brightness-125">{feature.label}</p>
                        </div>
                    </div>

                    {/* Details */}
                    <div className="space-y-5">
                        <p className="text-base sm:text-lg text-slate-300 leading-relaxed">{feature.sublabel}</p>

                        {/* Points (optional display on smaller screens if needed) */}
                        {feature.points && feature.points.length > 0 && (
                            <ul className="space-y-2.5">
                                {feature.points.map((point, idx) => (
                                    <li key={idx} className="flex items-start">
                                        <div className={`flex-shrink-0 bg-gradient-to-r ${feature.color} opacity-20 p-1 rounded-full mr-2.5 mt-1`}>
                                            {/* Simple check or chevron */}
                                            <ChevronRight className="w-3 h-3 text-white" />
                                        </div>
                                        <span className="text-sm text-slate-300">{point}</span>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {/* Tags */}
                        {feature.tags && feature.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 pt-3">
                                {feature.tags.map((tag, idx) => (
                                    <div
                                        key={idx}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-700/50 text-slate-300 text-xs hover:bg-slate-700 transition-all duration-300`}
                                    >
                                        {React.cloneElement(tag.icon, { className: "w-3.5 h-3.5" })}
                                        <span>{tag.text}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right SVG Container */}
                {feature.ProcessSVG && (
                    // Use remaining space on md+, set explicit aspect ratio
                    <div className="flex-1 md:max-w-[45%] mt-6 md:mt-0 flex items-center justify-center">
                        <div className="w-full aspect-[3/2] max-w-md mx-auto"> {/* Enforce 3:2 aspect ratio */}
                            {/* The SVG component will now fill this container */}
                            <feature.ProcessSVG />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};


// ... (rest of Features component: Features, useEffect, handleNavigate, handleMouseEnter, handleMouseLeave, JSX)
// IMPORTANT: Make sure you also have the necessary CSS for animations like
// animate-flow-down, animate-flow-right, animate-progress, animate-gradient-x, animate-spin-slow
// defined in your CSS file (e.g., index.css or App.css)
const Features = () => {
    const [activeFeature, setActiveFeature] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const timeoutRef = useRef(null);

    // Auto-advance the active feature every 7 seconds
    useEffect(() => {
        if (!isPaused) {
            timeoutRef.current = setTimeout(() => {
                setActiveFeature((prev) => (prev + 1) % features.length);
            }, 7000);
        }

        return () => clearTimeout(timeoutRef.current);
    }, [activeFeature, isPaused]);

    // Handle navigation to other pages
    const handleNavigate = (path) => {
        console.log("Navigating to:", path);
        // Implement actual navigation logic here
    };

    // Handle hover over a feature
    const handleMouseEnter = (index) => {
        setActiveFeature(index);
        setIsPaused(true);
    };

    // Handle mouse leave
    const handleMouseLeave = () => {
        setIsPaused(false);
    };

    return (
        <section className="relative z-10 py-24 px-4 overflow-hidden">
            {/* Background effects */}
            <div className="absolute inset-0 bg-gradient-to-b from-blue-900/5 to-amber-900/5"></div>



            <div className="max-w-7xl mx-auto relative">
                <div className="text-center mb-16">
                    <div className="inline-block px-4 py-1.5 bg-gradient-to-r from-blue-900/30 to-amber-900/30 rounded-full text-blue-400 text-sm font-medium mb-4 border border-blue-700/30">
                        <div className="flex items-center gap-2">
                            <Droplets className="w-4 h-4" />
                            <span>Data Is Oil</span>
                            <span className="mx-1">�</span>
                            <span>Fuel Your Business Growth</span>
                            <Fuel className="w-4 h-4 text-amber-400" />
                        </div>
                    </div>
                    <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight max-w-4xl mx-auto">
                        <span className="text-white">Transform Your Data into </span>
                        <span className="bg-gradient-to-r from-blue-400 to-amber-400 bg-clip-text text-transparent animate-gradient-x">Business Fuel</span>
                    </h2>
                    <p className="text-xl text-slate-300 leading-relaxed mb-10 max-w-3xl mx-auto">
                        Just as oil powers the modern economy, refined data drives business growth.
                        We help you extract, refine, and utilize your most valuable untapped resource.
                    </p>

                    {/* Value indicator */}
                    <div className="inline-flex items-center gap-3 px-4 py-2 bg-slate-800/80 rounded-full border border-slate-700/50 mb-8">
                        {features.map((feature, idx) => (
                            <button
                                key={idx}
                                className={`w-3 h-3 rounded-full transition-all duration-300 ${activeFeature === idx ? 'bg-gradient-to-r ' + feature.color + ' shadow-md ' + feature.shadowColor : 'bg-slate-600'}`}
                                onClick={() => setActiveFeature(idx)}
                                aria-label={`View ${feature.title}`}
                            ></button>
                        ))}
                    </div>
                </div>

                {/* Features Content */}
                <div className="relative max-w-6xl mx-auto" onMouseLeave={handleMouseLeave}>
                    {/* Oil pipeline visualization */}
                    <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-10 bg-gradient-to-b from-slate-800 via-slate-800 to-slate-800 transform -translate-x-1/2 rounded-full border-l border-r border-blue-500/30">
                        {/* Pipeline segments */}
                        {[...Array(12)].map((_, i) => (
                            <div
                                key={i}
                                className="absolute w-full h-6 border-t border-b border-blue-500/30"
                                style={{ top: `${i * 8}%` }}
                            ></div>
                        ))}

                        {/* Data flow animation */}
                        <PipelineFlow active={true} />
                    </div>

                    {/* Feature Cards */}
                    <div className="space-y-24 relative z-10">
                        {features.map((feature, index) => (
                            <div key={feature.id} className="relative">
                                {/* Connector to pipeline - horizontal pipeline */}
                                <div className="hidden md:block absolute top-1/2 -left-14 h-8 w-20 bg-gradient-to-r from-slate-800 to-slate-800 transform -translate-y-1/2 rounded-full border border-blue-500/30">
                                    {/* Connector segments */}
                                    {[...Array(3)].map((_, i) => (
                                        <div
                                            key={i}
                                            className="absolute h-full w-4 border-l border-r border-blue-500/30"
                                            style={{ left: `${i * 25}%` }}
                                        ></div>
                                    ))}

                                    {/* Data flow in connector */}
                                    {activeFeature === index && (
                                        <div className="absolute inset-0 overflow-hidden">
                                            {[...Array(5)].map((_, i) => (
                                                <div
                                                    key={i}
                                                    className="absolute h-2 w-2 rounded-full bg-gradient-to-r from-blue-400 to-amber-400 animate-flow-right"
                                                    style={{
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        left: '-10%',
                                                        animationDelay: `${i * 0.5}s`,
                                                        animationDuration: '2s'
                                                    }}
                                                ></div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Valve indicator for active feature */}
                                {activeFeature === index && (
                                    <div className="hidden md:flex absolute -left-24 top-1/2 transform -translate-y-1/2 items-center justify-center">
                                        <div className={`relative w-16 h-16 rounded-full border-4 border-slate-800 ${feature.shadowColor.replace('/20', '/50')} flex items-center justify-center overflow-hidden`}>
                                            <div className={`absolute inset-0 bg-gradient-to-r ${feature.color} opacity-20`}></div>
                                            <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${feature.color} flex items-center justify-center animate-spin-slow`}>
                                                <Gauge className="w-8 h-8 text-white" />
                                            </div>
                                        </div>
                                        <div className="absolute w-4 h-10 bg-slate-800 rounded-full border border-blue-500/30 flex items-center justify-center">
                                            <div className="w-1 h-6 bg-blue-500/30 rounded-full"></div>
                                        </div>
                                    </div>
                                )}

                                <div className={`transform transition-all duration-500 ${activeFeature === index ? 'scale-100 opacity-100' : 'scale-95 opacity-70'}`}>
                                    <FeatureCard
                                        feature={feature}
                                        active={activeFeature === index}
                                        onNavigate={handleNavigate}
                                        onMouseEnter={() => handleMouseEnter(index)}
                                    />
                                </div>

                                {/* Progress indicator */}
                                {activeFeature === index && (
                                    <div className="absolute -right-4 bottom-10 hidden md:block">
                                        <div className="relative h-24 w-2 bg-slate-700/50 rounded-full overflow-hidden">
                                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-blue-500 to-amber-500 animate-progress rounded-full" style={{ animationDuration: "7s" }}></div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Action button */}
                    <div className="flex justify-center mt-16">
                        <button
                            className="bg-gradient-to-r from-blue-800 to-blue-900 hover:from-blue-900 hover:to-blue-950 text-white px-8 py-4 rounded-full font-medium flex items-center gap-2 transform transition-transform hover:scale-105 shadow-lg">
                            <span>Lets Dig</span>
                            <TrendingUp className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};


export default Features;